import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import { useAuth } from '../context/AuthContext';
import BiddingModal from '../components/BiddingModal';
import { FaEdit, FaTrash, FaExternalLinkAlt, FaFileAlt, FaGavel } from 'react-icons/fa';

function WorkerBidsPage() {
  const [bids, setBids] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bids');

  const [showBiddingModal, setShowBiddingModal] = useState(false);
  const [selectedBidForEdit, setSelectedBidForEdit] = useState(null);

  const fetchData = useCallback(async () => {
    if (!user?.profileId) return;
    setLoading(true);
    try {
      const [bidsRes, appsRes] = await Promise.all([
        apiClient.get(`/bids/worker/${user.profileId}`),
        apiClient.get(`/jobs/worker/${user.profileId}/applications`)
      ]);
      setBids(bidsRes.data);
      setApplications(appsRes.data);
    } catch (err) {
      setError('Failed to load your data.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.profileId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditClick = (bid) => {
    setSelectedBidForEdit(bid);
    setShowBiddingModal(true);
  };

  const handleWithdrawClick = async (bidId) => {
    if (window.confirm('Are you sure you want to withdraw this bid?')) {
      try {
        await apiClient.delete(`/api/bids/${bidId}`);
        alert('Bid withdrawn successfully.');
        setBids(prevBids => prevBids.filter(bid => bid.id !== bidId));
      } catch (err) {
        alert('Failed to withdraw bid.');
        console.error(err);
      }
    }
  };

  if (loading) return <p className="text-center p-10">Loading...</p>;
  if (error) return <p className="text-center p-10 text-red-500">{error}</p>;

  return (
    <>
      <div className="container mx-auto p-4">
        <h1 className="text-3xl font-bold text-center mb-8">My Activities</h1>

        {/* Tabs */}
        <div className="flex justify-center mb-6 space-x-4">
          <button
            onClick={() => setActiveTab('bids')}
            className={`px-6 py-2 rounded-full font-semibold flex items-center ${activeTab === 'bids' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            <FaGavel className="mr-2" /> My Bids
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-6 py-2 rounded-full font-semibold flex items-center ${activeTab === 'applications' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            <FaFileAlt className="mr-2" /> My Applications
          </button>
        </div>

        <div className="space-y-4">
          {activeTab === 'bids' && (
            bids.length > 0 ? (
              bids.map(bid => (
                <div key={bid.id} className="bg-white rounded-lg shadow-md p-4 flex flex-col md:flex-row justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{bid.job?.title || 'Job Title Missing'}</h3>
                    <p className="text-gray-600">Your Bid Amount: <span className="font-bold text-green-600">{bid.bidAmount} BDT</span></p>
                    <p className="text-gray-500">Status: <span className={`font-semibold ${bid.status === 'accepted' ? 'text-green-500' : 'text-yellow-500'}`}>{bid.status || 'Pending'}</span></p>
                  </div>
                  <div className="flex items-center space-x-2 mt-4 md:mt-0">
                    <button onClick={() => navigate(`/job/${bid.jobId}`)} className="text-blue-500 hover:underline flex items-center"><FaExternalLinkAlt className="mr-1" /> View Job</button>
                    <button onClick={() => handleEditClick(bid)} className="text-gray-600 hover:underline flex items-center"><FaEdit className="mr-1" /> Edit</button>
                    <button onClick={() => handleWithdrawClick(bid.id)} className="text-red-500 hover:underline flex items-center"><FaTrash className="mr-1" /> Withdraw</button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">You have not placed any bids.</p>
            )
          )}

          {activeTab === 'applications' && (
            applications.length > 0 ? (
              applications.map(app => (
                <div key={app.id} className="bg-white rounded-lg shadow-md p-4 flex flex-col md:flex-row justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{app.job_details?.job_post?.title || 'Job Title Missing'}</h3>
                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">{app.cover_letter}</p>
                    <p className="text-gray-500 mt-2">Status: <span className={`font-semibold ${app.status === 'accepted' ? 'text-green-500' : 'text-yellow-500'}`}>{app.status || 'Pending'}</span></p>
                  </div>
                  <div className="flex items-center space-x-2 mt-4 md:mt-0">
                    <button onClick={() => navigate(`/job/${app.job_details?.job_post?.id}`)} className="text-blue-500 hover:underline flex items-center"><FaExternalLinkAlt className="mr-1" /> View Job</button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">You have not applied for any jobs.</p>
            )
          )}
        </div>
      </div>

      {showBiddingModal && (
        <BiddingModal
          show={showBiddingModal}
          onClose={() => { setShowBiddingModal(false); setSelectedBidForEdit(null); }}
          jobId={selectedBidForEdit?.jobId}
          isEditMode={true}
          existingBidData={selectedBidForEdit}
        />
      )}
    </>
  );
}

export default WorkerBidsPage;
